import type { HintCaret } from '@lite-workspace/shared/helm-ui/hint';

/*
 * Вид карточки-подсказки — одним местом. Его рисует `onboarding-coach-card`, и его же берут гиды
 * с собственным движком (гид админки): движок свой, карточка та же. Другой вид — правка здесь, для всех.
 */

/** Плита карточки. На экране уже 336px она сужается до экрана без его полей (8px с каждой стороны). */
export const COACH_PLATE = `w-80 max-w-[var(--max-w-coach-viewport)] rounded-[var(--r-m)] border border-coach-hair
  bg-coach-surface text-coach-ink shadow-[var(--shadow-coach)]`;

/** Заголовок на плите. Правый отступ — воздух под крестиком закрытия. */
export const COACH_TITLE = 'pr-9 text-base leading-5 font-semibold text-coach-ink';

export const COACH_BODY = 'px-4.5 pt-1.5 text-sm leading-5 text-coach-body';

/** Нижний ряд: счётчик слева, кнопки шагов справа. */
export const COACH_FOOTER = 'flex items-center justify-between px-4.5 pt-3 pb-2.5';

/** Та грань карточки, на которой сидит уголок: на ней же он берёт скруглённый угол и обводку. */
const CARET_SIDE = {
  top: '-top-1.5 rounded-tl-[var(--r-compact)] border-t border-l',
  bottom: '-bottom-1.5 rounded-br-[var(--r-compact)] border-r border-b',
  // Карточка сбоку от иконки вертикального ряда: уголок на боковой грани, смотрит на иконку.
  left: '-left-1.5 rounded-bl-[var(--r-compact)] border-b border-l',
  right: '-right-1.5 rounded-tr-[var(--r-compact)] border-t border-r',
  // Карточка стоит сбоку от окна своего места: на якорь показывает само окно, а уголок карточки
  // смотрел бы в его стенку.
  none: 'hidden',
} as const;

/**
 * Место уголка по горизонтали. Съехавшая от края экрана карточка держит середину якоря в 28px от своей
 * ближней грани (`CARD_CARET_INSET_PX`, `hlm-hint.ts`) — уголок шириной 12px встаёт туда серединой,
 * то есть краем на 28 − 6 = 22px.
 */
const CARET_ALIGN = {
  // Середина — полем, а не сдвигом: сдвиг задвинутой карточки едет тем же свойством `translate` и
  // затёр бы центровку собой.
  center: 'left-1/2 -ml-1.5',
  start: 'left-5.5',
  end: 'right-5.5',
} as const;

/** То же по вертикали — у уголка на боковой грани. */
const CARET_ALIGN_Y = {
  center: 'top-1/2 -mt-1.5',
  start: 'top-5.5',
  end: 'bottom-5.5',
} as const;

export type CoachCaretAlign = keyof typeof CARET_ALIGN;

/**
 * Уголок к якорю на грани `side`. Место на боковой грани считается по вертикали, на верхней и нижней —
 * по горизонтали.
 */
export function coachCaret(side: HintCaret, align: CoachCaretAlign): string {
  const along = side === 'left' || side === 'right' ? CARET_ALIGN_Y : CARET_ALIGN;

  return `absolute size-3 rotate-45 border-coach-hair bg-coach-surface ${CARET_SIDE[side]} ${along[align]}`;
}
